import React from "react";
import styled from "styled-components";
import { SurveySegment, SurveySegmentFilterAlias, Survey, PublicSurveyReportLink, PortalSharedSurveyReportView, SegmentReport, surveySegmentListCompare, segmentListsOfListsMatch, surveySegmentCompare, rng, getSegmentListKey2 } from "../../../../../../../amorg-common";
import { sharedPortalResourceClientApi } from "../../../../../../../api/portalManagementClientApi";
import { createSurveyReportLink, deleteSurveyReportLink, deleteSurveySegmentFilterAlias, updateSurveySegmentFilterAlias, createSurveySegmentFilterAlias } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { ButtonRow } from "../../../../../../../designSystem/button/ButtonRow";
import { AnimatedCollapsibleContent } from "../../../../../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { colors } from "../../../../../../../designSystem/colors/color";
import { ConfirmationModal } from "../../../../../../../designSystem/compositeComponents/ConfirmationButton";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { PopupMenuButton } from "../../../../../../../designSystem/compositeComponents/PopupMenuButton";
import { TextField } from "../../../../../../../designSystem/formField/text/TextField";
import { Icon } from "../../../../../../../designSystem/icons/Icons";
import { OnOff } from "../../../../../../../designSystem/inputs/OnOff";
import { MessageCard } from "../../../../../../../designSystem/Message";
import { Modal } from "../../../../../../../designSystem/Modal";
import { tableStyles } from "../../../../../../../designSystem/styles/tableStyles";
import { SegmentListChip } from "../../_components/SegmentListChip";
import { Span14 } from "../../../../../../../designSystem/typography/Body";
import { CopyButton } from "../../../../../../../designSystem/compositeComponents/CopyLinkButton";
import { Link } from "../../../../../../../designSystem/Link";

type SegmentationListRecord = {
    segments: SurveySegment[][],
    inPortal: boolean,
    inLinks?: { url:string };
    alias?: SurveySegmentFilterAlias;
}

type SurveyReportViewListProps = {
    siteId: string,
    survey:Survey,
    surveyLinks: PublicSurveyReportLink[],
    portalReports: PortalSharedSurveyReportView[],
    aliases: SurveySegmentFilterAlias[],
    segmentReports: SegmentReport[],
    selectedSegmentFilter?: SurveySegment[],
    onChanged: () => void,
    onSelectSegmentFilter: (segmentFilter: SurveySegment[][]) => void,
    onLoadingChange: (loading: boolean) => void
};
export function SurveyReportViewList (props: SurveyReportViewListProps): JSX.Element
{
    const [linkPendingDelete, setLinkPendingDelete] = React.useState<PublicSurveyReportLink | null>(null);
    const [viewPendingDelete, setViewPendingDelete] = React.useState<SurveySegment[][] | null>(null);
    const [inEditView, setInEditView] = React.useState<{ aliasId: string | null, name: string, segmentFilter: SurveySegment[][], errorMessage?:string } | null>(null);
    const [allOpen, setAllOpen] = React.useState(false);

    const segmentFiltersInUse = React.useMemo(() =>
    {
        function cloneSegmentFilter(segmentFilter: SurveySegment[][]): SurveySegment[][]
        {
            return segmentFilter.map(segmentList => segmentList.map(segment => ({ ...segment })));
        }

        function compareSegmentFilters(lhs: SurveySegment[][], rhs: SurveySegment[][]): number
        {
            if (lhs.length !== rhs.length)
            {
                return lhs.length - rhs.length;
            }

            for (let i = 0; i < lhs.length; i++)
            {
                const compareValue = surveySegmentListCompare(props.survey.segmentations, lhs[i], rhs[i]);
                if (compareValue !== 0)
                {
                    return compareValue;
                }
            }

            return 0;
        }

        const items: SegmentationListRecord[] = props.surveyLinks.map(link => ({
            segments: cloneSegmentFilter(link.configuration.segmentFilter),
            inPortal: false,
            inLinks: { url: `${window.location.protocol}//${window.location.host}/surveys/publicreport/${link.id}@${props.siteId}` }
        }));

        for (const portalReport of props.portalReports)
        {
            const item = items.find(r => segmentListsOfListsMatch(r.segments, portalReport.segmentFilter));
            if (item)
            {
                item.inPortal = true;
            }
            else
            {
                items.push({
                    segments: cloneSegmentFilter(portalReport.segmentFilter),
                    inPortal: true,
                    inLinks: undefined
                });
            }
        }

        for(const alias of props.aliases)
        {
            const item = items.find(r => segmentListsOfListsMatch(r.segments, alias.segmentFilter));
            if (item)
            {
                item.alias = alias;
            }
            else
            {
                items.push({
                    segments: cloneSegmentFilter(alias.segmentFilter),
                    inPortal: false,
                    inLinks: undefined,
                    alias
                });
            }
        }

        for(const item of items)
        {
            for(const segments of item.segments)
            {
                segments.sort((a, b) => surveySegmentCompare(props.survey.segmentations, a, b));
            }

            item.segments.sort((a, b) => surveySegmentListCompare(props.survey.segmentations, a, b));
        }

        items.sort((a, b) => compareSegmentFilters(a.segments, b.segments));

        return items;
    }, [props.surveyLinks, props.portalReports, props.aliases]);

    async function handleClickShareInPortal(segments: SurveySegment[][], active: boolean)
    {
        props.onLoadingChange(true);
        if(active)
        {
            await sharedPortalResourceClientApi.sharePortalResource(
                rng.getRandomId(),
                props.survey.id,
                {
                    resourceType:'SurveyReport',
                    config: {
                        segmentFilter: segments,
                    }
                }
            );
        }
        else{
            const report = props.portalReports.find(r => segmentListsOfListsMatch(r.segmentFilter, segments));
            await sharedPortalResourceClientApi.unsharePortalResource(report?.id ?? '');
        }
        props.onChanged();
        props.onLoadingChange(false);
    }

    async function handleClickShareLink(segments: SurveySegment[][], active: boolean)
    {
        props.onLoadingChange(true);
        if(active)
        {
            await createSurveyReportLink(
                props.survey.id,
                rng.getRandomId(),
                segments
            );
            props.onChanged();
        }
        else
        {
            const link = props.surveyLinks.find(l => segmentListsOfListsMatch(l.configuration.segmentFilter, segments));
            if(link)
            {
                setLinkPendingDelete(link);
            }
        }
        props.onLoadingChange(false);
    }

    async function handleConfirmDeleteLink()
    {
        if(!linkPendingDelete) return;
        props.onLoadingChange(true);
        await deleteSurveyReportLink(linkPendingDelete.id);
        setLinkPendingDelete(null);
        props.onChanged();
        props.onLoadingChange(false);
    }

    async function handleConfirmDeleteView()
    {
        if(!viewPendingDelete) return;

        props.onLoadingChange(true);

        const aliasId = props.aliases.find(a => segmentListsOfListsMatch(a.segmentFilter, viewPendingDelete))?.id;
        if(aliasId)        {
            await deleteSurveySegmentFilterAlias(props.survey.id, aliasId);
        }

        const link = props.surveyLinks.find(l => segmentListsOfListsMatch(l.configuration.segmentFilter, viewPendingDelete));
        if(link)
        {
            await deleteSurveyReportLink(link.id);
        }

        const portalReport = props.portalReports.find(r => segmentListsOfListsMatch(r.segmentFilter, viewPendingDelete));
        if(portalReport)
        {
            await sharedPortalResourceClientApi.unsharePortalResource(portalReport.id);
        }

        setViewPendingDelete(null);
        props.onChanged();
        props.onLoadingChange(false);
    }

    async function handleSaveAlias(name: string, segmentFilter: SurveySegment[][])
    {
        if(!inEditView) return;
        props.onLoadingChange(true);
        if(inEditView.aliasId)
        {
            const result = await updateSurveySegmentFilterAlias(
                props.survey.id,
                inEditView.aliasId,
                name,
                segmentFilter
            );
            if(result.isSuccess)
            {
                setInEditView(null);
            }
            else
            {
                let errorMessage = 'Der skete en fejl under opdateringen af visningen';
                if(result.errorMessage === 'updateSurveySegmentFilterAlias_invalid_name')
                    errorMessage = 'Ugyldigt navn';
                else if(result.errorMessage === 'updateSurveySegmentFilterAlias_name_in_use')
                    errorMessage = 'Navnet er allerede i brug for en anden visning';
                else if(result.errorMessage === 'updateSurveySegmentFilterAlias_segmentFilter_in_use')
                    errorMessage = 'En visning med det valgte segmentfilter eksisterer allerede';

                setInEditView({ ...inEditView, errorMessage: errorMessage });
            }
        }
        else
        {
            const result = await createSurveySegmentFilterAlias(
                props.survey.id,
                rng.getRandomId(),
                name,
                segmentFilter
            );
            if(result.isSuccess)            {
                setInEditView(null);
            }
            else            
            {
                let errorMessage = 'Der skete en fejl under oprettelsen af visningen';
                if(result.errorMessage === 'createSurveySegmentFilterAlias_invalid_name')
                    errorMessage = 'Ugyldigt navn';
                else if(result.errorMessage === 'createSurveySegmentFilterAlias_name_in_use')
                    errorMessage = 'Navnet er allerede i brug for en anden visning';
                else if(result.errorMessage === 'createSurveySegmentFilterAlias_segmentFilter_in_use')
                    errorMessage = 'En visning med det valgte segmentfilter eksisterer allerede';

                setInEditView({ ...inEditView, errorMessage: errorMessage });
            }
        }
        props.onChanged();
        props.onLoadingChange(false);
    }

    return (

        <div>
            <ConfirmationModal
                showModal={linkPendingDelete !== null}
                title="Slet rapportlink"
                content="Er du sikker på, at du vil slette rapportlinket? Handlingen kan ikke fortrydes."
                requireKey={false}
                buttonText="Slet"
                confirmButtonVariant="destructive"
                onConfirm={handleConfirmDeleteLink}
                onCancel={() => setLinkPendingDelete(null)}
            />
     
            <ConfirmationModal
                showModal={viewPendingDelete !== null}
                title="Slet visning"
                content="Er du sikker på, at du vil slette visningen? Handlingen kan ikke fortrydes, og alle delinger af visningen vil blive fjernet."
                requireKey={false}
                buttonText="Slet"
                confirmButtonVariant="destructive"
                onConfirm={handleConfirmDeleteView}
                onCancel={() => setViewPendingDelete(null)}
            />

            <EditAliasModal
                show={inEditView !== null}
                initialName={inEditView?.name ?? ''}
                initialSegmentFilter={inEditView?.segmentFilter ?? []}
                segmentReports={props.segmentReports}
                errorMessage={inEditView?.errorMessage}
                onConfirm={handleSaveAlias}
                onCancel={() => setInEditView(null)}
            />

            <div>
                <ViewGridRow style={{paddingBottom: '8px'}}>
                    <Flex alignItems="center" gap={4}>
                        <OnOff active={allOpen} onClick={setAllOpen} />
                        <Span14>Vis alle detaljer</Span14>
                    </Flex>
                    <div />
                    <Flex justifyContent="center" alignItems="center">
                        <Icon name="captive_portal" color={colors.icon.active} toolTipText="Delt i medarbejderportalen" />
                    </Flex>
                    <Flex justifyContent="center" alignItems="center">
                        <Icon name="interface_actions_link" color={colors.icon.active} toolTipText="Delt som link" />
                    </Flex>
                    <div />
                    <div />
                </ViewGridRow>

                {segmentFiltersInUse.map((filter, index) =>
                    <ViewGridRow key={index} borderTop>
                        <FilterRowHeader record={filter} defaultShow={allOpen} />
                        <div>
                            <Button variant="text" text="Vælg" toolTipText="Vælg som aktiv rapportvisning" size="32px"
                                onClick={() => props.onSelectSegmentFilter(filter.segments)} />
                        </div>
                        <Flex justifyContent="center" alignItems="flex-start" style={{paddingTop:'4px'}}>
                            <OnOff active={filter.inPortal} onClick={(active) => handleClickShareInPortal(filter.segments, active)} />
                        </Flex>
                        <Flex justifyContent="center" alignItems="flex-start" style={{paddingTop:'4px'}}>
                            <OnOff active={!!filter.inLinks} onClick={(active) => handleClickShareLink(filter.segments, active)} />
                        </Flex>
                        <div>
                            <CopyButton value={filter.inLinks?.url ?? ''} disabled={!filter.inLinks?.url} />
                        </div>
                        <div>
                            <PopupMenuButton size="32px" entries={[
                                { 
                                    type: 'menuItem', 
                                    text: 'Rediger', 
                                    icon: 'interface_actions_edit', 
                                    onClick: () => {
                                        setInEditView({ aliasId: filter.alias?.id ?? null, name: filter.alias?.name ?? '', segmentFilter: filter.segments });
                                    }
                                },
                                { 
                                    type: 'menuItem', 
                                    text: 'Slet', 
                                    icon:'interface_actions_delete',
                                    onClick: () => {
                                        setViewPendingDelete(filter.segments);
                                    }
                                }
                            ]} />
                        </div>
                    </ViewGridRow>
                )}

            </div>
        </div>

    );
}



type FilterRowHeaderProps = {
    record: SegmentationListRecord,
    defaultShow?: boolean
};
function FilterRowHeader (props: FilterRowHeaderProps): JSX.Element
{
    const [show, setShow] = React.useState(props.defaultShow || false);

    React.useEffect(() => {
        if(props.defaultShow !== undefined)
        {
            setShow(props.defaultShow);
        }
    }, [props.defaultShow]);

    if (props.record.segments.length > 0)
    {
        return (
            <Flex flexDirection="column" gap={4} justifyContent="flex-start" alignItems="flex-start">
                <Flex alignItems="center" justifyContent="flex-start">
                    <Button variant="text" text="" size="32px" icon={show ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'} onClick={() => setShow(s => !s)} />
                    <Span14>{props.record?.alias?.name || "Uden navn"}</Span14>
                </Flex>

                <AnimatedCollapsibleContent open={show}>
                    <Flex flexDirection="column" gap={8} alignItems="flex-start">
                        <Flex gap={4} style={{ flexWrap: 'wrap' }}>
                            {props.record.segments.map((list, index) =>
                                <SegmentListChip key={index} identifier={getSegmentListKey2(list)} segments={list} selected={false} />
                            )}
                        </Flex>
                        {props.record.inLinks &&
                            <Link href={props.record.inLinks.url} target="_blank" rel="noopener noreferrer" style={{fontSize:'14px'}}>
                                {props.record.inLinks.url}
                            </Link>
                        }
                    </Flex>
                </AnimatedCollapsibleContent>
            </Flex>
        );

    }

    return <></>;
}

type EditAliasModalProps = {
    show: boolean;
    initialName: string;
    initialSegmentFilter: SurveySegment[][];
    segmentReports: SegmentReport[];
    errorMessage?: string;
    onConfirm: (name: string, segmentFilter: SurveySegment[][]) => void;
    onCancel: () => void;
};

export function EditAliasModal(props: EditAliasModalProps): JSX.Element
{
    const [name, setName] = React.useState(props.initialName);
    const [segmentFilter, setSegmentFilter] = React.useState<SurveySegment[][]>(props.initialSegmentFilter);

    React.useEffect(() => {
        if (props.show)
        {
            setName(props.initialName);
            setSegmentFilter(props.initialSegmentFilter);
        }
    }, [props.show, props.initialName, props.initialSegmentFilter]);

    return (
        <Modal
            showModal={props.show}
            title="Rediger visning"
            onCancel={props.onCancel}
            content={
                <Flex flexDirection="column" gap={16}>
                    {props.errorMessage &&
                        <MessageCard variant="error">
                            {props.errorMessage}
                        </MessageCard>
                    }
                    <TextField
                        label="Navn"
                        value={name}
                        onChange={(value) => setName(value)}
                    />
                    {/* <EditableSegmentPanel
                        segmentLists={props.segmentReports}
                        selection={segmentFilter}
                        onSelectionChange={setSegmentFilter}
                    /> */}
                    <ButtonRow>
                        <Button variant="text" text="Annuller" onClick={props.onCancel} />
                        <Button variant="filled" text="Gem" onClick={() => props.onConfirm(name, segmentFilter)} />
                    </ButtonRow>
                </Flex>
            }
        />
    );
}

const ViewGridRow = styled.div<{borderTop?: boolean}>`
    display: grid;
    column-gap: 16px;
    padding: 2px 0px;
    grid-template-columns: minmax(0, 1fr) min-content repeat(4, 40px);
    border-top: ${props => props.borderTop ? tableStyles(props.theme.colors).border : ''};
`;